import React, { useState, useEffect, useRef } from 'react';
import type { ReasoningBlock } from '../../store/chatStore';
import { ChatMarkdown } from '@plerionhq/design-system/unstable';
import { ShimmerText } from '../ChatArea/Indicators';
import { useTranslation } from 'react-i18next';
interface ReasoningContentProps {
  content: ReasoningBlock;
  isLastMessage?: boolean;
  isLastContent?: boolean;
  isLoading?: boolean;
}

export const ReasoningContent: React.FC<ReasoningContentProps> = ({
  content,
  isLastMessage = false,
  isLastContent = false,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/chat');
  // By default, collapse the reasoning section
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );
  const startTimeRef = useRef<number>(Date.now());
  const [reasoningDuration, setReasoningDuration] = useState<string | null>(
    null,
  );
  const durationCalculatedRef = useRef<boolean>(false);

  // Format duration helper function
  const formatDuration = (duration: number): string => {
    if (duration < 1000) {
      return `${duration}ms`;
    } else if (duration < 60000) {
      return `${(duration / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(duration / 60000);
      const seconds = ((duration % 60000) / 1000).toFixed(0);
      return `${minutes}m ${seconds}s`;
    }
  };

  // If this is the last message and new content is being streamed, keep it open
  useEffect(() => {
    if (isLastMessage && isLastContent && isLoading) {
      setIsOpen(true);
    } else if (!isLastMessage || !isLastContent) {
      // When closing programmatically, calculate duration if not already done
      if (!durationCalculatedRef.current) {
        const duration = Date.now() - startTimeRef.current;
        setReasoningDuration(formatDuration(duration));
        durationCalculatedRef.current = true;
      }
      setIsOpen(false);
    }
  }, [isLastMessage, isLastContent, isLoading]);

  // Simple toggle function for user clicks
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Update content height for smooth animations
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight + 10);
    } else {
      setContentHeight(0);
    }
  }, [isOpen, content.reasoning]);

  // Get the appropriate label based on state
  const getLabel = () => {
    if (isOpen && !reasoningDuration) {
      return t('reasoning');
    }

    return `Reasoned for ${reasoningDuration}`;
  };

  return (
    <div className="my-3 overflow-hidden">
      <div
        className="flex cursor-pointer items-center rounded-sm py-2 transition-colors"
        onClick={handleToggle}
      >
        <div className="flex items-center gap-2">
          {!reasoningDuration ? (
            <ShimmerText text={t('reasoning')} />
          ) : (
            <span className="text-black dark:text-white">{getLabel()}</span>
          )}
        </div>
        <svg
          className={`ml-2 h-4 w-4 text-gray-500 transition-transform duration-300 dark:text-gray-400 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 8.25l-7.5 7.5-7.5-7.5"
          />
        </svg>
      </div>
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          height: contentHeight === undefined ? 'auto' : `${contentHeight}px`,
        }}
      >
        <div
          ref={contentRef}
          className="border-l-2 border-gray-200 py-4 pl-4 dark:border-gray-700"
        >
          <div
            className={`prose prose-sm max-w-none text-gray-500 dark:text-gray-400`}
          >
            <ChatMarkdown
              content={content.reasoning}
              wrapperClassName="!text-gray-500 dark:text-gray-400"
              textAnimation={{
                animate: isLastMessage && isLastContent && isLoading,
                duration: 20,
                letterPerDuration: 5,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
